import React, { useState, useMemo, useEffect } from "react";
import { Card, Button } from "@aws-amplify/ui-react";
import { IoBookmarkSharp } from "react-icons/io5";
import { GrDownload } from "react-icons/gr";
import { PiTrash } from "react-icons/pi";
import Modal from "../components/Modal/Modal";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

import { getHistoryApi, deleteHistoryApi, downloadHistoryFile } from "../services/AxiosServices";
import { logError } from "../utils/errorHandling";

const HistoryTable = ({ isAIHubPage }) => {

    const { getAccessTokenSilently } = useAuth0();
    const [sortBy, setSortBy] = useState("Recently");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [chatToDelete, setChatToDelete] = useState<{ id: number, name: string } | null>(null);
    const [historyData, setHistoryData] = useState<any[]>([]);
    const sortOptions = ["Recently", "Oldest", "Solution Type"];

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const token = await getAccessTokenSilently();
                if (token) {
                    const historyResponse = await getHistoryApi(token);
                    if (historyResponse && (historyResponse as any).result) {
                        setHistoryData((historyResponse as any).result);
                    }
                }
            } catch (error) {
                logError("History fetch failed", error);
            }
        };
        void fetchHistory();
    }, [getAccessTokenSilently]);

    const handleSortChange = () => {
        const currentIndex = sortOptions.indexOf(sortBy);
        const nextIndex = (currentIndex + 1) % sortOptions.length;
        setSortBy(sortOptions[nextIndex]);
    };

    const handleDeleteItemClick = (item: { id: number, name: string }) => {
        setChatToDelete(item);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (chatToDelete) {
            try {
                const token = await getAccessTokenSilently();
                if (token) {
                    if (historyData.some(historyItem => historyItem.item_id === chatToDelete.id)) {
                        const response = await deleteHistoryApi(chatToDelete.id, token);
                        if (response && typeof response === 'object' && 'status' in response && 'deleted_item' in response) {
                            if (response.status === 'success') {
                                setHistoryData(prevData => prevData.filter(item => item.item_id !== response.deleted_item));
                            }
                        } else {
                            console.error("Unexpected response format:", response);
                        }
                    } else {
                        //chat delete
                    }
                }
            } catch (error) {
                logError("History delete failed", error);
            }
            setShowDeleteModal(false);
            setChatToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setChatToDelete(null);
    };

    const handleDownloadItem = async (item: any) => {
        if (item.downloadableData) {
            try {
                const token = await getAccessTokenSilently();
                const response = await downloadHistoryFile(item.itemId, token);
                const disposition = response.headers.get("Content-Disposition");
                let fileName = item.itemName || `download-${item.itemId}`;
                if (disposition && disposition.includes("filename=")) {
                    const match = disposition.match(/filename="(.+)"/);
                    if (match?.[1]) {
                        fileName = match[1];
                    }
                }
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(blobUrl);
            } catch (error) {
                logError("History download failed", error);
            }
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays === 1) {
            return 'Yesterday';
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return `${Math.floor(diffInDays / 7)} week${Math.floor(diffInDays / 7) > 1 ? 's' : ''} ago`;
        }
    };

    const processedHistoryData = useMemo(() => {
        let processed = historyData.map((item) => ({
            itemType: item.item_subscript || 'N/A',
            itemName: item.item_name || 'Untitled',
            solutionType: item.solution_type || 'Unknown',
            date: formatDate(item.creation_date),
            creationDate: new Date(item.creation_date),
            status: item.status,
            downloadableData: item.object_key,
            itemId: item.item_id
        }));

        switch (sortBy) {
            case "Recently":
                processed.sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime());
                break;
            case "Oldest":
                processed.sort((a, b) => a.creationDate.getTime() - b.creationDate.getTime());
                break;
            case "Solution Type":
                processed.sort((a, b) => a.solutionType.localeCompare(b.solutionType));
                break;
        }

        return processed;
    }, [historyData, sortBy]);

    // AutoQA/GenQA items route to /qanewproject; everything else keeps the /preaudit flow.
    const handleItemNavigation = (item: any) => {
        if (!item?.itemName) return;
        const solutionType = item?.solutionType?.toLowerCase();
        if (["autoqa", "genqa", "genq&a"].includes(solutionType)) {
            const [firstPart] = item.itemName.split(" - ").map((part) => part.trim());
            window.open(`/qanewproject/${encodeURIComponent(firstPart)}`, "_blank");
        } else {
            window.open(`/preaudit?project_name=${encodeURIComponent(item.itemName)}`, "_blank");
        }
    };

    return (
        <div>

            <Card className="generated-items-section">
                <div className="section-header">
                    <h2>Generated items</h2>
                    <div className="sort-control">
                        <span>Sort by</span>
                        <Button
                            variation="link"
                            className="sort-button"
                            onClick={handleSortChange}
                        >
                            {sortBy} ▼
                        </Button>
                    </div>
                </div>

                {processedHistoryData.length === 0 ? (
                    <div className="no-data-container">
                        <div className="no-data-message">
                            <img src={`${process.env.PUBLIC_URL}/no-data.jpg`} alt="No history" className="no-data-image" />
                            <h3>No history to show yet!</h3>
                            <p>Generate your first item in the AI Solution Hub, and it will pop up here.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="history-table-container">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Action</th>
                                        <th>Solution type</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(isAIHubPage ? processedHistoryData.slice(0, 5) : processedHistoryData).map((item, index) => (
                                        <tr
                                            key={item.itemId || index}
                                            onClick={() => handleItemNavigation(item)}
                                        >
                                            <td 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleItemNavigation(item);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="item-details">
                                                    <strong>{item.itemName}</strong>
                                                    <div className="item-type">{item.itemType}</div>
                                                </div>
                                            </td>
                                            <td>
                                                {!item.downloadableData ? (
                                                    <span className="generating-status">{item.status}</span>
                                                ) : (
                                                    <div className="action-buttons">
                                                        <button
                                                            className="action-button bookmark"
                                                            title="Save"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleItemNavigation(item);
                                                            }}
                                                        >
                                                            <IoBookmarkSharp />
                                                        </button>

                                                        <button
                                                            className="action-button download"
                                                            title="Download"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleDownloadItem(item);
                                                            }}
                                                        >
                                                            <GrDownload />
                                                        </button>

                                                        <button
                                                            className="action-button delete"
                                                            title="Delete"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleDeleteItemClick({
                                                                    id: item.itemId,
                                                                    name: item.itemName,
                                                                });
                                                            }}
                                                        >
                                                            <PiTrash />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td>{item.solutionType}</td>
                                            <td>
                                                <div className="date-info">
                                                    {item.date}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {isAIHubPage && (
                            <div className="show-more-button">
                                <Link to="/history">
                                    <Button variation="link">
                                        Show Full History <span className="down-arrow">▼</span>
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </>

                )}
            </Card>
            <Modal isOpen={showDeleteModal} onClose={handleCancelDelete}>
                <div className="delete-confirmation-modal">
                    <p>Are you sure you want to delete "{chatToDelete?.name}"?</p>
                    <div className="modal-buttons">
                        <button className="cancel-btn" onClick={handleCancelDelete}>
                            Cancel
                        </button>
                        <button className="delete-btn" onClick={handleConfirmDelete}>
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default HistoryTable;
